import { Box, Card, CardContent, Fade, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { OnboardingTrack } from "../types";

const OptionCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ theme, selected }) => ({
  cursor: "pointer",
  transition: "all 0.25s ease",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main + "10" : "transparent",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    borderColor: theme.palette.primary.main,
  },
}));

const trackOptions: {
  value: OnboardingTrack;
  icon: string;
  title: string;
  description: string;
  perfectFor: string;
}[] = [
  {
    value: "business",
    icon: "Building2",
    title: "For my business",
    description: "I already have an established business with a website",
    perfectFor: "Established companies, agencies, SaaS",
  },
  {
    value: "startup",
    icon: "Rocket",
    title: "Looking to start a business",
    description: "I'm starting a new business and want to use AI to launch faster",
    perfectFor: "Aspiring entrepreneurs, side hustlers, new ventures",
  },
];

interface TrackSelectionStepProps {
  appName: string;
  selectedTrack: OnboardingTrack | null;
  onSelect: (track: OnboardingTrack) => void;
}

export default function TrackSelectionStep({
  appName,
  selectedTrack,
  onSelect,
}: TrackSelectionStepProps) {
  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
          How will you use {appName}?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          Start free — no credit card required. Pick the option that fits you best and
          we'll tailor everything to you.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {trackOptions.map((option) => {
            const selected = selectedTrack === option.value;
            return (
              <OptionCard
                key={option.value}
                selected={selected}
                onClick={() => onSelect(option.value)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: selected ? "primary.main" : "action.hover",
                        color: selected ? "primary.contrastText" : "text.secondary",
                      }}
                    >
                      <LucideIcon name={option.icon} size={26} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: selected ? "primary.main" : "text.primary",
                        }}
                      >
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {option.description}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block", fontStyle: "italic" }}
                      >
                        Perfect for: {option.perfectFor}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </OptionCard>
            );
          })}
        </Box>
      </Box>
    </Fade>
  );
}
